import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BusinessIcon from '@mui/icons-material/Business';
import CodeIcon from '@mui/icons-material/Code';
import DateRangeIcon from '@mui/icons-material/DateRange';
import LaunchIcon from '@mui/icons-material/Launch';
import PersonIcon from '@mui/icons-material/Person';
import WebIcon from '@mui/icons-material/Web';
import WorkIcon from '@mui/icons-material/Work';
import { AnimatePresence } from 'framer-motion';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useAppState } from '../../../Context/AppStateContext';
import { useSanityData } from '../../../Context/SanityDataContext';
import { useSanityLocalized } from '../../../hooks/useSanityLocalized';
import type { SanityProject, SanityClientProject } from '../../../services/sanity/types';
import { theme } from '../../../theme';
import BorderBox from '../../ui/BorderBox.component';
import Modal from '../../ui/Modal.component';
import MotionContainer from '../../ui/MotionContainer.component';
import ScrollableContent from '../../ui/ScrollableContent.component';
import Timeline, {
  TimelineItem,
  ItemHeader,
  ItemTitle,
  ItemPeriod,
  ItemSubtitle,
  BadgeContainer,
  Badge,
  ItemFooter,
  Metadata,
  ActionHint,
} from '../../ui/Timeline.component';

interface ProjectsProps {
  visible: boolean;
  onClose: () => void;
  onFocus?: () => void;
  isActive?: boolean;
}

// ============ STYLED COMPONENTS ============

const ProjectsContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 20px;
  overflow: hidden;
`;

const LoadingText = styled.div`
  color: ${theme.colors.green};
  font-family: 'Courier New', monospace;
`;

// Detail view styles
const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 1px solid rgba(31, 207, 94, 0.3);
  color: ${theme.colors.green};
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  margin-bottom: 20px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(31, 207, 94, 0.1);
    border-color: ${theme.colors.green};
  }
`;

const DetailHeader = styled.div`
  margin-bottom: 25px;
`;

const DetailTitle = styled.h2`
  margin: 0 0 5px 0;
  color: ${theme.colors.green};
  font-family: 'Courier New', monospace;
  font-size: 22px;
`;

const DetailSubtitle = styled.p`
  margin: 0 0 8px 0;
  color: ${theme.colors.textMuted};
  font-size: 15px;
  font-style: italic;
`;

const DetailPeriod = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(31, 207, 94, 0.15);
  color: ${theme.colors.green};
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 13px;
  font-family: 'Courier New', monospace;
`;

const SectionTitle = styled.h3`
  color: ${theme.colors.green};
  margin: 25px 0 15px 0;
  font-size: 14px;
  font-family: 'Courier New', monospace;
  text-transform: uppercase;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid rgba(31, 207, 94, 0.2);
  padding-bottom: 8px;
`;

const AboutSection = styled.div`
  line-height: 1.7;
  color: ${theme.colors.text};

  p {
    margin: 0 0 12px 0;
    font-size: 14px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const RoleCard = styled.div`
  background: rgba(31, 207, 94, 0.03);
  border: 1px solid rgba(31, 207, 94, 0.15);
  border-radius: 6px;
  padding: 15px;
  margin-bottom: 15px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const RoleTitle = styled.h4`
  margin: 0 0 10px 0;
  color: ${theme.colors.green};
  font-family: 'Courier New', monospace;
  font-size: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RoleDescription = styled.p`
  margin: 0 0 12px 0;
  color: ${theme.colors.text};
  font-size: 14px;
  line-height: 1.6;
`;

const RoleDetails = styled.ul`
  margin: 0;
  padding-left: 20px;
  list-style: none;

  li {
    position: relative;
    margin-bottom: 8px;
    color: ${theme.colors.textMuted};
    font-size: 13px;
    line-height: 1.5;

    &::before {
      content: '>';
      position: absolute;
      left: -15px;
      color: ${theme.colors.green};
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const TechGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const TechTag = styled.span`
  background: rgba(31, 207, 94, 0.1);
  color: ${theme.colors.green};
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-family: 'Courier New', monospace;
`;

// Freelance styles
const FreelanceSection = styled.div`
  margin-top: 30px;
  padding-top: 20px;
  border-top: 2px solid rgba(31, 207, 94, 0.2);
`;

const FreelanceTitle = styled.h3`
  color: ${theme.colors.green};
  margin: 0 0 15px 0;
  font-family: 'Courier New', monospace;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FreelanceIntro = styled.div`
  margin-bottom: 20px;
  line-height: 1.6;
  color: ${theme.colors.text};

  p {
    margin: 0 0 10px 0;
    font-size: 14px;
  }
`;

const ClientGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 15px;
`;

const ClientCard = styled.div`
  background: rgba(31, 207, 94, 0.03);
  border: 1px solid rgba(31, 207, 94, 0.15);
  border-radius: 6px;
  padding: 15px;
`;

const ClientHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const ClientName = styled.h4`
  margin: 0;
  color: ${theme.colors.green};
  font-family: 'Courier New', monospace;
  font-size: 14px;
`;

const ClientType = styled.span`
  background: rgba(31, 207, 94, 0.15);
  color: ${theme.colors.green};
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 10px;
`;

const ClientSubtitle = styled.p`
  margin: 0 0 8px 0;
  color: ${theme.colors.textMuted};
  font-size: 12px;
  font-style: italic;
`;

const ClientDescription = styled.p`
  margin: 0 0 12px 0;
  color: ${theme.colors.text};
  font-size: 13px;
  line-height: 1.5;
`;

const VisitButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 1px solid ${theme.colors.green};
  color: ${theme.colors.green};
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(31, 207, 94, 0.1);
  }
`;

// ============ COMPONENT ============

const Projects: React.FC<ProjectsProps> = ({ visible, onClose, onFocus, isActive }) => {
  const { t } = useTranslation();
  const { isOnMobile } = useAppState();
  const { projects: sanityProjects, freelance, isLoading } = useSanityData();
  const { langCode, getLocalizedValue, getLocalizedText } = useSanityLocalized();
  const [selectedProject, setSelectedProject] = useState<SanityProject | null>(null);

  if (!visible) return null;

  // Show loading state
  if (isLoading) {
    return (
      <Modal
        color={theme.colors.green}
        header={t('modals.projects.header')}
        widthString={isOnMobile ? '90%' : '800px'}
        xPositionPercent={isOnMobile ? 50 : 40}
        yPositionPercent={20}
        onClose={onClose}
        onFocus={onFocus}
        isActive={isActive}
        icon={<WorkIcon />}
      >
        <ProjectsContainer>
          <LoadingText>&gt; {t('modals.projects.loading')}</LoadingText>
        </ProjectsContainer>
      </Modal>
    );
  }

  // Helper to get roles from a project
  const getRoles = (project: SanityProject): string[] => {
    return project.roles?.map(role => {
      const title = role.title?.[langCode as 'en' | 'no'] || role.title?.en || '';
      return title;
    }).filter(Boolean) || [];
  };

  // Transform projects to timeline items
  const timelineItems = sanityProjects.map((project) => ({
    id: project._id,
    title: project.name,
    subtitle: getLocalizedValue(project.subtitle),
    period: project.period,
    badges: getRoles(project),
    metadata: `${project.technology?.length || 0} ${t('modals.projects.sections.technologies').toLowerCase()}`,
    actionHint: `${t('modals.projects.viewDetails')} →`,
    onClick: () => setSelectedProject(project),
  }));

  // Render timeline overview
  const renderTimeline = () => (
    <MotionContainer animationKey="timeline">
      <Timeline
        items={timelineItems}
        renderItem={(item) => (
          <TimelineItem key={item.id} onClick={item.onClick} isClickable>
            <ItemHeader>
              <ItemTitle>
                <BusinessIcon fontSize="small" />
                {item.title}
              </ItemTitle>
              <ItemPeriod>{item.period}</ItemPeriod>
            </ItemHeader>
            {item.subtitle && <ItemSubtitle>{item.subtitle}</ItemSubtitle>}
            {item.badges && item.badges.length > 0 && (
              <BadgeContainer>
                {item.badges.map((badge, idx) => (
                  <Badge key={idx}>{badge}</Badge>
                ))}
              </BadgeContainer>
            )}
            <ItemFooter>
              <Metadata>{item.metadata}</Metadata>
              <ActionHint>{item.actionHint}</ActionHint>
            </ItemFooter>
          </TimelineItem>
        )}
      />

      {/* Freelance section at bottom of timeline */}
      {freelance && freelance.clientProjects?.length > 0 && (
        <FreelanceSection>
          <FreelanceTitle>
            <WebIcon />
            {getLocalizedValue(freelance.title)}
          </FreelanceTitle>
          {freelance.introduction && freelance.introduction.length > 0 && (
            <FreelanceIntro>
              {freelance.introduction.map((intro, idx) => (
                <p key={intro._key || idx}>{getLocalizedText(intro)}</p>
              ))}
            </FreelanceIntro>
          )}
          <ClientGrid>
            {freelance.clientProjects.map((client: SanityClientProject) => (
              <ClientCard key={client._key}>
                <ClientHeader>
                  <ClientName>{client.clientName}</ClientName>
                  <ClientType>{getLocalizedValue(client.projectType)}</ClientType>
                </ClientHeader>
                <ClientSubtitle>{getLocalizedValue(client.subtitle)}</ClientSubtitle>
                <ClientDescription>{getLocalizedValue(client.description)}</ClientDescription>
                {client.url && (
                  <VisitButton onClick={() => window.open(client.url!, '_blank')}>
                    {t('modals.projects.visitSite')} <LaunchIcon fontSize="small" />
                  </VisitButton>
                )}
              </ClientCard>
            ))}
          </ClientGrid>
        </FreelanceSection>
      )}
    </MotionContainer>
  );

  // Render detail view for selected project
  const renderDetail = (project: SanityProject) => (
    <MotionContainer animationKey={`detail-${project._id}`}>
      <BackButton onClick={() => setSelectedProject(null)}>
        <ArrowBackIcon fontSize="small" />
        {t('modals.projects.backToTimeline')}
      </BackButton>

      <BorderBox isActive={true} noBackground={true}>
        <DetailHeader>
          <DetailTitle>{project.name}</DetailTitle>
          <DetailSubtitle>{getLocalizedValue(project.subtitle)}</DetailSubtitle>
          <DetailPeriod>
            <DateRangeIcon fontSize="small" />
            {project.period}
          </DetailPeriod>
        </DetailHeader>

        {/* About section - project descriptions */}
        {project.projectDescription && project.projectDescription.length > 0 && (
          <>
            <SectionTitle>
              <BusinessIcon fontSize="small" />
              {t('modals.projects.sections.about')}
            </SectionTitle>
            <AboutSection>
              {project.projectDescription.map((desc, idx) => (
                <p key={desc._key || idx}>{getLocalizedText(desc)}</p>
              ))}
            </AboutSection>
          </>
        )}

        {/* My Roles section */}
        {project.roles && project.roles.length > 0 && (
          <>
            <SectionTitle>
              <PersonIcon fontSize="small" />
              {t('modals.projects.sections.myRoles')}
            </SectionTitle>
            {project.roles.map((role, idx) => {
              const roleTitle = role.title?.[langCode as 'en' | 'no'] || role.title?.en || '';
              const roleDesc = role.description?.[langCode as 'en' | 'no'] || role.description?.en || '';
              const roleDetails = role.details?.map(d =>
                d[langCode as 'en' | 'no'] || d.en || ''
              ).filter(Boolean) || [];

              return (
                <RoleCard key={role._key || idx}>
                  <RoleTitle>
                    <WorkIcon fontSize="small" />
                    {roleTitle}
                  </RoleTitle>
                  {roleDesc && <RoleDescription>{roleDesc}</RoleDescription>}
                  {roleDetails.length > 0 && (
                    <RoleDetails>
                      {roleDetails.map((detail, dIdx) => (
                        <li key={dIdx}>{detail}</li>
                      ))}
                    </RoleDetails>
                  )}
                </RoleCard>
              );
            })}
          </>
        )}

        {/* Technologies section */}
        {project.technology && project.technology.length > 0 && (
          <>
            <SectionTitle>
              <CodeIcon fontSize="small" />
              {t('modals.projects.sections.technologies')}
            </SectionTitle>
            <TechGrid>
              {project.technology.map((tech, idx) => (
                <TechTag key={idx}>{tech}</TechTag>
              ))}
            </TechGrid>
          </>
        )}
      </BorderBox>
    </MotionContainer>
  );

  return (
    <Modal
      color={theme.colors.green}
      header={selectedProject ? selectedProject.name : t('modals.projects.header')}
      widthString={isOnMobile ? '90%' : '800px'}
      xPositionPercent={isOnMobile ? 50 : 40}
      yPositionPercent={20}
      onClose={onClose}
      onFocus={onFocus}
      isActive={isActive}
      icon={<WorkIcon />}
    >
      <ProjectsContainer>
        <ScrollableContent>
          <AnimatePresence mode="wait">
            {selectedProject ? renderDetail(selectedProject) : renderTimeline()}
          </AnimatePresence>
        </ScrollableContent>
      </ProjectsContainer>
    </Modal>
  );
};

export default Projects;
